import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import {
  LuDatabase,
  LuFileText,
  LuFilter,
  LuListFilter,
  LuPlus,
  LuSearch,
} from "react-icons/lu";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import EmployeeFormModal from "../../components/employees/EmployeeFormModal";
import EmployeeList from "../../components/employees/EmployeeList";
import Pagination from "../../components/pagination";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import cx from "../../components/ui/cx";
import {
  actionBarClass,
  buttonSpinnerClass,
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardTitleClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  pageTitleClass,
  summaryLabelClass,
  summaryValueClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchEmployeesListData,
  getEditEmployee,
  getEmployees,
  getTotalEmployees,
  getTotalRemainingPayment,
  getTotalRemainingWithdrawal,
  setEditEmployee,
  toggleEmployeeModal,
} from "../../features/employees/employeesSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { DELETE, GET, POST, PUT } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { IFormInput } from "../../types/employees";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "કામદાર" },
];

const formatAmount = (value: number) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const isFixedOptions = [
  { value: 1, label: "ફિક્સ નથી" },
  { value: 2, label: "ફિક્સ છે" },
];

const reportPeriodOptions = [
  { value: "all", label: "બધો સમય" },
  { value: "month", label: "મહિનો પસંદ કરો" },
];

const monthOptions = [
  { value: "01", label: "જાન્યુઆરી" },
  { value: "02", label: "ફેબ્રુઆરી" },
  { value: "03", label: "માર્ચ" },
  { value: "04", label: "એપ્રિલ" },
  { value: "05", label: "મે" },
  { value: "06", label: "જૂન" },
  { value: "07", label: "જુલાઈ" },
  { value: "08", label: "ઓગસ્ટ" },
  { value: "09", label: "સપ્ટેમ્બર" },
  { value: "10", label: "ઓક્ટોબર" },
  { value: "11", label: "નવેમ્બર" },
  { value: "12", label: "ડિસેમ્બર" },
];

const firstReportYear = 2024;
const yearOptions = Array.from(
  { length: new Date().getFullYear() + 1 - firstReportYear + 1 },
  (_, i) => {
    const year = String(new Date().getFullYear() + 1 - i);
    return { value: year, label: year };
  }
);

const currentMonthValue = () =>
  String(new Date().getMonth() + 1).padStart(2, "0");

const listKey = "employees";

const EmployeesPage = () => {
  const dispatch = useAppDispatch();
  const employees = useAppSelector(getEmployees);
  const totalEmployees = useAppSelector(getTotalEmployees);
  const editEmployee = useAppSelector(getEditEmployee);
  const totalRemainingPayment = useAppSelector(getTotalRemainingPayment);
  const totalRemainingWithdrawal = useAppSelector(getTotalRemainingWithdrawal);
  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.employeesCreate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.employeesReport
  );

  const toolbarCount = 1 + (canReport ? 1 : 0) + (canCreate ? 1 : 0);

  interface FilterFormInput {
    name: string;
    fromDate: string;
    toDate: string;
    is_fixed: number | null;
  }

  const emptyFilters: FilterFormInput = {
    name: "",
    fromDate: "",
    toDate: "",
    is_fixed: null,
  };

  const { handleSubmit, register, reset, getValues, control, setValue } =
    useForm<FilterFormInput>({ defaultValues: savedList.filters });

  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );
  const [showReportFilter, setShowReportFilter] = useState<boolean>(
    !!savedList.showReportFilter
  );
  const [reportPeriod, setReportPeriod] = useState<"all" | "month">(
    savedList.reportPeriod || "all"
  );
  const [reportMonth, setReportMonth] = useState<string>(
    (savedList.reportPeriod === "month" && savedList.reportMonth) ||
      currentMonthValue()
  );
  const [reportYear, setReportYear] = useState<string>(
    (savedList.reportPeriod === "month" && savedList.reportYear) ||
      String(new Date().getFullYear())
  );

  const fetchId = useRef(0);

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleShowEmployeeForm = async () => {
    await dispatch(setEditEmployee(null));
    dispatch(toggleEmployeeModal(true));
  };

  const handleSaveEmployee = async (data: IFormInput) => {
    try {
      if (editEmployee && editEmployee.id) {
        await PUT(`/v1/admin/employees/${editEmployee.id}`, {
          employee_code: data.employee_code,
          name: data.name,
          phone: data.phone,
          address: data.address,
          amount: data.amount,
          is_fixed: data.is_fixed,
          status: data.status,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              await dispatch(setEditEmployee(null));

              if (page === 1) {
                fetchEmployeesList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      } else {
        await POST("/v1/admin/employees", {
          employee_code: data.employee_code,
          name: data.name,
          phone: data.phone,
          address: data.address,
          amount: data.amount,
          is_fixed: data.is_fixed,
          status: data.status,
          created_at: data.created_at,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              if (page === 1) {
                fetchEmployeesList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      }
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleDeleteEmployee = async (id: number) => {
    try {
      await DELETE(`/v1/admin/employees/${id}`)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = employees.length > 1 ? page : page - 1;
            if (page === pageNumber || pageNumber < 1) {
              fetchEmployeesList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleOnFilterSubmit = async () => {
    if (page === 1) {
      fetchEmployeesList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleClearFilter = () => {
    reset(emptyFilters);
    if (page === 1) {
      fetchEmployeesList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const fetchEmployeesList = async (page: number, size: number) => {
    const data = getValues();
    const currentFetch = ++fetchId.current;

    dispatch(saveListState({ key: listKey, value: { filters: data } }));
    setIsLoading(true);

    dispatch(
      fetchEmployeesListData({
        page: page,
        size: size,
        search: data?.name || null,
        fromDate: data?.fromDate || null,
        toDate: data?.toDate || null,
        is_fixed: data?.is_fixed || null,
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / size)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  };

  const handleClearReportFilter = () => {
    setReportPeriod("all");
    setReportMonth(currentMonthValue());
    setReportYear(String(new Date().getFullYear()));
  };

  const handleGenerateSalaryReport = async () => {
    try {
      setIsReportLoading(true);
      const params =
        reportPeriod === "month"
          ? { month: `${reportYear}-${reportMonth}` }
          : {};

      await GET(`/v1/admin/employees/salary/reports`, params)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });

            dispatch(updatePdfFile(response.data.data));
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsReportLoading(false);
    }
  };

  // Coming back to the list (from a record's page, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({
        key: listKey,
        value: {
          page,
          pageSize,
          showFilter,
          showReportFilter,
          reportPeriod,
          reportMonth,
          reportYear,
        },
      })
    );
  }, [
    dispatch,
    page,
    pageSize,
    showFilter,
    showReportFilter,
    reportPeriod,
    reportMonth,
    reportYear,
  ]);

  useEffect(() => {
    fetchEmployeesList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>કામદાર</h1>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className={actionBarClass}>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <div className="flex items-center gap-2">
              <span className={summaryLabelClass}>ટોટલ બાકી પગાર:</span>
              <span className={summaryValueClass}>
                {formatAmount(totalRemainingPayment)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className={summaryLabelClass}>ટોટલ બાકી ઉપાડ:</span>
              <span className={summaryValueClass}>
                {formatAmount(totalRemainingWithdrawal)}
              </span>
            </div>
          </div>

          <div
            className={cx(
              "grid w-full gap-2 sm:flex sm:w-auto",
              toolbarCount === 1 ? "grid-cols-1" : "grid-cols-2"
            )}
          >
            {canReport && (
              <Button
                variant="outline"
                onClick={() => {
                  if (!showReportFilter === true) setShowFilter(false);
                  setShowReportFilter(!showReportFilter);
                }}
              >
                <LuFileText className="h-4 w-4" />
                પગાર રિપોર્ટ
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => {
                if (!showFilter === true) setShowReportFilter(false);
                setShowFilter(!showFilter);
              }}
            >
              <LuFilter className="h-4 w-4" />
              ફિલ્ટર
            </Button>

            {canCreate && (
              <Button
                variant="primary"
                className={cx(toolbarCount === 3 && "col-span-2")}
                onClick={handleShowEmployeeForm}
              >
                <LuPlus className="h-4 w-4" />
                નવો કામદાર ઉમેરો
              </Button>
            )}
          </div>
        </div>

        {showReportFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>પગાર રિપોર્ટ</div>
            </div>

            <div className={cardContentClass}>
              <div className={filterGridClass}>
                <div className="space-y-2">
                  <label className={filterLabelClass}>સમયગાળો</label>
                  <div className="relative">
                    <LuDatabase className={fieldIconClass} />
                    <Select
                      styles={iconSelectStyles}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="સમયગાળો"
                      options={reportPeriodOptions}
                      value={
                        reportPeriodOptions.find(
                          (val) => val.value === reportPeriod
                        ) || null
                      }
                      onChange={(val: any) =>
                        setReportPeriod(val?.value === "month" ? "month" : "all")
                      }
                    />
                  </div>
                </div>

                {reportPeriod === "month" && (
                  <>
                    <div className="space-y-2">
                      <label className={filterLabelClass}>મહિનો</label>
                      <div className="relative">
                        <LuDatabase className={fieldIconClass} />
                        <Select
                          styles={iconSelectStyles}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="મહિનો"
                          options={monthOptions}
                          value={
                            monthOptions.find(
                              (val) => val.value === reportMonth
                            ) || null
                          }
                          onChange={(val: any) =>
                            val?.value && setReportMonth(val.value)
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className={filterLabelClass}>વર્ષ</label>
                      <div className="relative">
                        <LuDatabase className={fieldIconClass} />
                        <Select
                          styles={iconSelectStyles}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="વર્ષ"
                          options={yearOptions}
                          value={
                            yearOptions.find(
                              (val) => val.value === reportYear
                            ) || null
                          }
                          onChange={(val: any) =>
                            val?.value && setReportYear(val.value)
                          }
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className={filterFooterClass}>
                <Button
                  type="button"
                  variant="outline"
                  className="max-sm:flex-1"
                  onClick={handleClearReportFilter}
                >
                  ફિલ્ટર સાફ કરો
                </Button>

                <Button
                  type="button"
                  variant="primary"
                  className="max-sm:flex-1"
                  onClick={handleGenerateSalaryReport}
                  disabled={isReportLoading}
                >
                  {isReportLoading && <span className={buttonSpinnerClass} />}
                  રિપોર્ટ બનાવો
                </Button>
              </div>
            </div>
          </div>
        )}

        {showFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>ફિલ્ટર</div>
            </div>

            <div className={cardContentClass}>
              <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ થી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("fromDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ સુધી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("toDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>શોધો</label>
                    <div className="relative">
                      <LuSearch className={fieldIconClass} />
                      <Input
                        placeholder="વિગતો દ્વારા શોધો"
                        className={fieldInputClass()}
                        {...register("name")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>ફિક્સ</label>
                    <div className="relative">
                      <LuListFilter className={fieldIconClass} />
                      <Controller
                        {...register("is_fixed")}
                        control={control}
                        render={({ field }) => (
                          <Select
                            isClearable
                            {...field}
                            value={
                              isFixedOptions.find(
                                (val) => val.value === field.value
                              ) || null
                            }
                            styles={iconSelectStyles}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="ફિક્સ સિલેક્ટ કરો"
                            options={isFixedOptions}
                            onChange={(val: any) =>
                              setValue("is_fixed", val?.value ?? null)
                            }
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className={filterFooterClass}>
                  <Button
                    type="button"
                    variant="outline"
                    className="max-sm:flex-1"
                    onClick={handleClearFilter}
                  >
                    ફિલ્ટર સાફ કરો
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    className="max-sm:flex-1"
                  >
                    ફિલ્ટર લાગુ કરો
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <EmployeeList
          employees={employees}
          pageNumber={page}
          pageSize={pageSize}
          isLoading={isLoading}
          handleDeleteEmployee={handleDeleteEmployee}
        />

        {!isLoading && employees.length > 0 && (
          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalEmployees}
            paginate={paginate}
            currentPage={page}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="કામદાર"
          />
        )}
      </div>

      <EmployeeFormModal handleSaveEmployee={handleSaveEmployee} />
    </AppLayout>
  );
};
export default EmployeesPage;
